import type { IChallenge } from "@bbj-types/challenge";
import { yupResolver } from "@hookform/resolvers/yup";
import { TextFieldForm } from "@inputs/hook-form";
import { Divider, Stack } from "@mui/material";
import { useI18nContext } from "@root/utils/organisation";
import { useMemo, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { ButtonAddObjective } from "../../ButtonAddObjective";
import type { TPaperCandidateChallengeChallenge, TPaperCandidateChallengeObjective } from "../types";
import { PaperCandidateChallengeEditButtons } from "./buttons";
import {
  type IFormEditCandidateChallenge,
  type IResolverEditCandidateChallenge,
  emptyObjective,
  formatDefaultValues,
  formatSubmitValues,
  resolver,
} from "./logics";
import { PaperCandidateChallengeEditObjective } from "./objective";

export interface IPaperCandidateChallengeEditFormProps<
  CandidateObjective extends TPaperCandidateChallengeObjective,
  CandidateChallenge extends TPaperCandidateChallengeChallenge<CandidateObjective>,
> {
  disabled?: boolean;
  disableCreateObjective?: boolean;
  challenge: Pick<IChallenge, "objectives">;
  candidateChallenge: CandidateChallenge;
  onSubmit: (candidateChallenge: CandidateChallenge) => void | Promise<void>;
  onCancel: () => void;
}

export const PaperCandidateChallengeEditForm = <
  CandidateObjective extends TPaperCandidateChallengeObjective,
  CandidateChallenge extends TPaperCandidateChallengeChallenge<CandidateObjective>,
>({
  disabled,
  disableCreateObjective,
  challenge,
  candidateChallenge,
  onSubmit,
  onCancel,
}: IPaperCandidateChallengeEditFormProps<CandidateObjective, CandidateChallenge>) => {
  const [loading, setLoading] = useState(false);
  const { timeZone } = useI18nContext();
  const { control, handleSubmit } = useForm<
    IFormEditCandidateChallenge<CandidateObjective>,
    void,
    IResolverEditCandidateChallenge<CandidateObjective>
  >({
    defaultValues: formatDefaultValues<CandidateObjective, CandidateChallenge>(challenge, candidateChallenge),
    // biome-ignore lint/suspicious/noExplicitAny: YupResolver type issue
    resolver: yupResolver(resolver(timeZone)) as any,
  });
  const { fields, append, remove } = useFieldArray({ control, name: "objectives" });

  const activeObjectives = useMemo(() => {
    return challenge.objectives.filter((obj) => obj.active);
  }, [challenge.objectives]);

  const submit = async (data: IResolverEditCandidateChallenge<CandidateObjective>) => {
    setLoading(true);
    try {
      await onSubmit(formatSubmitValues(candidateChallenge, data));
    } finally {
      setLoading(false);
    }
  };

  const isDisabled = disabled || loading;

  return (
    <Stack sx={{ gap: 2 }}>
      <TextFieldForm
        control={control}
        name="description"
        label="Description"
        size="small"
        multiline
        rows={4}
        fullWidth
      />
      <Divider flexItem orientation="horizontal" />
      {fields.map((field, index) => (
        <PaperCandidateChallengeEditObjective
          key={field.id}
          control={control}
          index={index}
          source={field.source}
          objectives={activeObjectives}
          disabled={isDisabled}
          onRemove={() => remove(index)}
        />
      ))}
      {!disableCreateObjective && (
        <ButtonAddObjective onClick={() => append(emptyObjective<CandidateObjective>())} disabled={isDisabled} />
      )}
      <PaperCandidateChallengeEditButtons
        disabled={isDisabled}
        loading={loading}
        onCancel={onCancel}
        onSubmit={handleSubmit(submit)}
      />
    </Stack>
  );
};
